import Vue from "vue"
import Vuex from "vuex"
import lodash from "lodash"
import * as providersDataService from "@/Providers/ProvidersDataService"
import * as appDataService from "@/AppDataService"
import AppService from "@/AppService"
import * as mutations from "./constants/mutation-types"
import * as actions from "./constants/action-types"
import * as getters from "./constants/getter-types"
import * as constants from "@/Shared/constants"
import { metadataRules } from "./metadataRules"
import { configService } from "./configService"
import { xvat } from "./xvat"

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    mpxUserInfo: {},
    cableUserInfo: {},
    mvatsAuthorizeInfo: {},
    opsUIAuthorizeInfo: {},
    serviceRegistry: [],
    providers: [],
    providerGroups: [],
    mpxParentAccount: {},
    mpxSubAccounts: {}
  },
  mutations: {
    [mutations.SET_MPX_USER_INFO](state, mpxUserInfo) {
      state.mpxUserInfo = mpxUserInfo
    },
    [mutations.SET_CABLE_USER_INFO](state, cableUserInfo) {
      state.cableUserInfo = cableUserInfo
    },
    [mutations.SET_MVATS_AUTHORIZE_INFO](state, mvatsAuthorizeInfo) {
      state.mvatsAuthorizeInfo = mvatsAuthorizeInfo
    },
    [mutations.SET_OPSUI_AUTHORIZE_INFO](state, opsUIAuthorizeInfo) {
      state.opsUIAuthorizeInfo = opsUIAuthorizeInfo
    },
    [mutations.SET_PROVIDERS](state, providersInput) {
      state.providers = providersInput
    },
    [mutations.SET_RPVODER_GROUPS](state, providerGroupsInput) {
      state.providerGroups = providerGroupsInput
    },
    [mutations.SET_MPX_PARENT_ACCOUNT](state, mpxParentAccount) {
      state.mpxParentAccount = mpxParentAccount
    },
    [mutations.SET_MPX_SUB_ACCOUNTS](state, mpxSubAccounts) {
      state.mpxSubAccounts = mpxSubAccounts
    },
    [mutations.SET_SERVICE_REGISTRY](state, services) {
      state.serviceRegistry = services
    }
  },
  actions: {
    [actions.GET_MPX_USER_INFO](context) {
      return new Promise(resolve => {
        appDataService.getMPXUserInfo().then(response => {
          context.commit(mutations.SET_MPX_USER_INFO, response)
          resolve()
        })
      })
    },
    [actions.GET_CABLE_USER_INFO](context) {
      return new Promise(resolve => {
        appDataService.getCableUserInfo().then(response => {
          context.commit(mutations.SET_CABLE_USER_INFO, response)
          resolve()
        })
      })
    },
    [actions.GET_MVATS_AUTHORIZE_INFO](context) {
      return new Promise(resolve => {
        appDataService.getMVATSAuthorizeInfo().then(response => {
          context.commit(mutations.SET_MVATS_AUTHORIZE_INFO, response)
          resolve()
        })
      })
    },
    [actions.GET_OPSUI_AUTHORIZE_INFO](context) {
      return new Promise(resolve => {
        appDataService.getOpsUIAuthorizeInfo().then(response => {
          context.commit(mutations.SET_OPSUI_AUTHORIZE_INFO, response)
          resolve()
        })
      })
    },
    [actions.GET_MPX_PARENT_ACCOUNT](context) {
      return new Promise(resolve => {
        appDataService.getMPXParentAccountInfo().then(response => {
          context.commit(mutations.SET_MPX_PARENT_ACCOUNT, response)
          resolve()
        })
      })
    },
    [actions.GET_MPX_SERVICE_REGISTRY](context) {
      return new Promise(resolve => {
        appDataService.getServiceRegistry().then(response => {
          context.commit(mutations.SET_SERVICE_REGISTRY, response)
          resolve()
        })
      })
    },
    [actions.GET_MPX_SUB_ACCOUNTS](context) {
      return new Promise(resolve => {
        appDataService.getMPXSubAccounts().then(response => {
          context.commit(mutations.SET_MPX_SUB_ACCOUNTS, response)
          resolve()
        })
      })
    },
    [actions.GET_PROVIDERS](context, queryObject) {
      let serviceURL = context.getters.VOD_SYCN_PREINGEST_DATA_SERVICE_URL
      if (lodash.isUndefined(queryObject)) {
        queryObject = {}
      }
      let accountIdsString = AppService.getSubAccountsIdsString(
        context.state.mpxSubAccounts
      )
      if (!lodash.isEmpty(accountIdsString)) {
        queryObject["byOwnerId"] = accountIdsString
        return new Promise(resolve => {
          providersDataService
            .retrieveProviders(queryObject, serviceURL)
            .then(response => {
              context.commit(mutations.SET_PROVIDERS, response.data.entries)
              resolve()
            })
        })
      } else {
        return new Promise(resolve => {
          context.commit(mutations.SET_PROVIDERS, [])
          resolve()
        })
      }
    },
    [actions.GET_PROVIDER_GROUPS](context, queryObject) {
      let serviceURL = context.getters.VOD_SYCN_PREINGEST_DATA_SERVICE_URL
      if (lodash.isUndefined(queryObject)) {
        queryObject = {}
      }
      let subAcountsString = AppService.getSubAccountsIdsString(
        context.state.mpxSubAccounts
      )
      if (!lodash.isEmpty(subAcountsString)) {
        queryObject["byOwnerId"] = subAcountsString
        return new Promise(resolve => {
          providersDataService
            .retrieveProviderGroups(queryObject, serviceURL)
            .then(response => {
              context.commit(
                mutations.SET_RPVODER_GROUPS,
                response.data.entries
              )
              resolve()
            })
        })
      } else {
        // Return empty array if there are no sub accounts
        return new Promise(resolve => {
          context.commit(mutations.SET_RPVODER_GROUPS, [])
          resolve()
        })
      }
    }
  },
  getters: {
    [getters.IS_SIGNED_INTO_CABLE](state) {
      return (
        !lodash.isEmpty(state.cableUserInfo) &&
        !lodash.isUndefined(state.cableUserInfo.access_token)
      )
    },
    [getters.IS_SIGNED_INTO_MPX](state) {
      return (
        !lodash.isEmpty(state.mpxUserInfo) &&
        !lodash.isUndefined(state.mpxUserInfo.token)
      )
    },
    [getters.HAS_ACCESS_TO_MPX_VIEW](state) {
      return AppService.hasAccessToMPXView(state.mpxParentAccount.service)
    },
    [getters.IS_POLARIS](state) {
      return AppService.isPolarisAccount(state.mpxParentAccount.id)
    },
    [getters.MVATS_WEB_SERVICE_URL](state) {
      return state.serviceRegistry[constants.MVATS_WEB_SERVICE]
    },
    [getters.MVATS_TRANSFORMATION_SERVICE_URL](state) {
      return state.serviceRegistry[constants.MVATS_TRANSFORMATION_SERVICE]
    },
    [getters.RULE_MGMT_DATA_SERVICE_URL](state) {
      return state.serviceRegistry[constants.RULE_MGMT_DATA_SERVICE]
    },
    [getters.VOD_SYCN_EVENT_DATA_SERVICE_URL](state) {
      return state.serviceRegistry[constants.VOD_SYCN_EVENT_DATA_SERVICE]
    },
    [getters.VOD_SYCN_OFFERING_DATA_SERVICE_URL](state) {
      return state.serviceRegistry[constants.VOD_SYCN_OFFERING_DATA_SERVICE]
    },
    [getters.VOD_SYCN_OFFERING_WEB_SERVICE_URL](state) {
      return state.serviceRegistry[constants.VOD_SYCN_OFFERING_WEB_SERVICE]
    },
    [getters.VOD_SYCN_PREINGEST_DATA_SERVICE_URL](state) {
      return state.serviceRegistry[constants.VOD_SYCN_PREINGEST_DATA_SERVICE]
    },
    [getters.VOD_SYCN_PREINGEST_WEB_SERVICE_URL](state) {
      return state.serviceRegistry[constants.VOD_SYCN_PREINGEST_WEB_SERVICE]
    },
    [getters.VOD_SYCN_TEMPLATE_DATA_SERVICE_URL](state) {
      return state.serviceRegistry[constants.VOD_SYCN_TEMPLATE_DATA_SERVICE]
    }
  },
  modules: {
    metadataRules: metadataRules,
    xvat: xvat,
    configService: configService
  }
})



// WEBPACK FOOTER //
// ./src/store/index.js